 
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Close } from "@mui/icons-material";
import { toast } from 'react-toastify';
import modal from "../../../../../assets/styles/components/modal.module.css";
import containerModal from "../../../../../assets/styles/components/containerModal.module.css";
import input from "../../../../../assets/styles/components/input.module.css";
import select from "../../../../../assets/styles/components/select.module.css";
import styles from "./AddCostCenterModal.module.css";

import { registerOrEditCostCenter, type CostCenterData } from "../../../../../services/financial";
import { type ICostCenter } from "../../CostCenters/CostCenterList";
import Button from "../../../../../components/Button/Button";

interface AddCostCenterModalProps {
    onClose: () => void;
    refetch?: () => void;
    entryToEdit?: ICostCenter;
    onSuccess?: (message: string) => void;
}

interface CostCenterFormData {
    uid_centro_custo?: string;
    nome: string;
    ativo: string;
}

const AddCostCenterModal = ({
    onClose,
    refetch,
    entryToEdit,
    onSuccess,
}: AddCostCenterModalProps) => {
    const queryClient = useQueryClient();
    const isEditing = !!entryToEdit;

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm<CostCenterFormData>({
        defaultValues: {
            nome: "",
            ativo: "true"
        }
    });

    useEffect(() => {
        if (entryToEdit) {
            setValue("nome", entryToEdit.nome);
            setValue("ativo", entryToEdit.ativo ? "true" : "false");
        } else {
            reset({
                nome: "",
                ativo: "true"
            });
        }
    }, [entryToEdit, setValue, reset]);

    const { mutateAsync, isPending } = useMutation({
        mutationFn: registerOrEditCostCenter,
        onSuccess: () => {
            const successMessage = isEditing ? "Centro de custo atualizado!" : "Centro de custo criado!";
            onSuccess?.(successMessage);
            queryClient.invalidateQueries({queryKey: ["costCenters"]});
            refetch?.();
            handleClose();
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || "Erro desconhecido.";
            toast.error(errorMessage);
        },
    });

    const onSubmit: SubmitHandler<CostCenterFormData> = async (data) => {
        try {
            const formattedData: CostCenterData = {
                uid_centro_custo: entryToEdit?.uid_centro_custo,
                nome: data.nome,
                ativo: data.ativo === "true"
            };

            await mutateAsync(formattedData);
        } catch (error) {
            console.error("Erro no formulário:", error);
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <form className={`${modal.containerModal} ${styles.containerProducts}`} onSubmit={handleSubmit(onSubmit)}>
            <div className={`${containerModal.containerModal} ${styles.products}`}>
                <h1>{isEditing ? "Editar Centro de Custo" : "Criar Novo Centro de Custo"}</h1>

                <div className={containerModal.close}>
                    <Button variant="contain" typeStyle={["small", "cancel"]} onClick={handleClose} type="button">
                        <Close />
                    </Button>
                </div>

                <div className={styles.row}>
                    <div className={styles.fieldGroup} style={{ flex: 3 }}>
                        <label htmlFor="nome">Nome *</label>
                        <input
                            id="nome"
                            className={input.input}
                            type="text"
                            placeholder="Descrição do centro de custo"
                            {...register("nome", { required: "O nome é obrigatório" })}
                        />
                        {errors.nome && <span className={styles.errorText}>{errors.nome.message}</span>}
                    </div>

                    <div className={styles.fieldGroup} style={{ flex: 1 }}>
                        <label htmlFor="ativo">Status</label>
                        <select
                            id="ativo"
                            className={`${input.input} ${select.select}`}
                            {...register("ativo")}
                        >
                            <option value="true">Ativo</option>
                            <option value="false">Inativo</option>
                        </select>
                    </div>
                </div>

                <div className={styles.buttonSave}>
                    <Button variant="contain" type="submit" disabled={isPending}>
                        {isPending ? "Salvando..." : "Salvar"}
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default AddCostCenterModal;